import { getCurrentUser } from "@/apis/user/route";
import { TermsAgreementType } from "@/apis/user/types";
import {
  LoginLayout,
  LoginLeftPanel,
  LoginRightPanel,
} from "@/components/login";
import { activateAccount } from "@/components/login/activate-account";
import { LoginForm } from "@/components/login/forms/login-form";
import { PasswordReset } from "@/components/login/forms/password-reset";
import { VerificationCode } from "@/components/login/forms/verification-code";
import {
  type CombinedFormData,
  combinedSchema,
  emailPasswordSchema,
} from "@/components/login/login-schema";
import { passwordResetSchema } from "@/components/login/password-reset-schema";
import {
  clearRecaptchaVerifier,
  completeMfaSignIn,
  signInWithPassword,
  signOutUser,
} from "@/services/auth-service";
import { zodResolver } from "@hookform/resolvers/zod";
import { MultiFactorResolver } from "firebase/auth";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Step 정의
enum LoginStep {
  EMAIL_PASSWORD = "email_password",
  VERIFICATION = "verification",
  PASSWORD_RESET = "password_reset",
}

const LoginPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<LoginStep>(
    LoginStep.EMAIL_PASSWORD,
  );
  const navigate = useNavigate();
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);

  // Clean up reCAPTCHA when component unmounts
  useEffect(() => {
    return () => {
      clearRecaptchaVerifier();
    };
  }, []);

  const getSchemaForStep = () => {
    switch (currentStep) {
      case LoginStep.EMAIL_PASSWORD:
        return emailPasswordSchema;
      case LoginStep.PASSWORD_RESET:
        return passwordResetSchema;
      default:
        return combinedSchema;
    }
  };
  // 각 step별 form 관리
  const form = useForm<CombinedFormData>({
    resolver: zodResolver(getSchemaForStep() as any),
    mode: currentStep === LoginStep.PASSWORD_RESET ? "onChange" : "onSubmit",
    defaultValues: {
      email: "",
      password: "",
      newPassword: "",
      confirmNewPassword: "",
      agreeTerms: false,
      agreePrivacy: false,
    },
  });

  // Step 이동 함수
  const goToStep = (step: LoginStep) => {
    setCurrentStep(step);
  };

  const [isLoading, setIsLoading] = useState(false);

  // 각 단계별 submit 핸들러
  const handleEmailPasswordSubmit = async (email: string, password: string) => {
    const result = await signInWithPassword(email, password);

    if (result.requiresMfa) {
      goToStep(LoginStep.VERIFICATION);
      setVerificationId(result.verificationId!);
      setResolver(result.resolver!);
    } else if (!result.success) {
      // Check if it's a session/timeout error
      if (
        result.error?.includes("시간이 초과") ||
        result.error?.includes("expired") ||
        result.error?.includes("session")
      ) {
        // Clear reCAPTCHA for fresh start
        await clearRecaptchaVerifier();

        // Reset form to allow retry
        setCurrentStep(LoginStep.EMAIL_PASSWORD);
      } else {
        form.setError("email", { message: result.error });
        form.setError("password", { message: result.error });
      }
    } else if (result.success) {
      navigate("/");
    }
  };

  const handleSubmit = async (data: Partial<CombinedFormData>) => {
    setIsLoading(true);
    try {
      switch (currentStep) {
        case LoginStep.EMAIL_PASSWORD:
          await handleEmailPasswordSubmit(data.email!, data.password!);
          break;
        case LoginStep.PASSWORD_RESET:
          await handlePasswordResetSubmit(data);
          break;
        default:
          break;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentUserWithRetry = async () => {
    const user = await getCurrentUser();
    if (user.status === 200 || user.isFirstLogin) return user;

    for (let i = 0; i < 3; i++) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      const user = await getCurrentUser();
      if (user.status === 200 || user.isFirstLogin) return user;
    }
    return user;
  };

  const handleVerificationSubmit = async (
    code: string,
  ): Promise<{ success: boolean; message?: string }> => {
    await completeMfaSignIn(resolver!, verificationId!, code);
    await new Promise((resolve) => setTimeout(resolve, 200));
    const user = await getCurrentUserWithRetry();

    if (user.status === 200) {
      navigate("/");
      return { success: true };
    } else if (user.isFirstLogin) {
      // User needs to set up their password

      // Navigate to password reset step
      goToStep(LoginStep.PASSWORD_RESET);

      // Return success to close the modal
      return { success: true };
    } else if (user.status === 401) {
      console.log("401");
    } else {
      signOutUser();
    }
    return { success: false, message: "Unknown error occurred" };
  };

  // 비밀번호 변경(Firebase)과 가입 기록(백엔드)은 원자적으로 묶을 수 없다.
  // 이전 시도에서 이미 설정한 비밀번호를 기억해, 재시도 시 Firebase 재호출을 건너뛴다.
  const passwordSetRef = useRef<string | null>(null);

  const handlePasswordResetSubmit = async (data: Partial<CombinedFormData>) => {
    const agreements: TermsAgreementType[] = [
      ...(data.agreeTerms ? (["service"] as const) : []),
      ...(data.agreePrivacy ? (["privacy"] as const) : []),
    ];
    if (agreements.length < 2) return;

    const result = await activateAccount({
      newPassword: data.newPassword!,
      agreements,
      passwordAlreadySet: passwordSetRef.current === data.newPassword,
    });

    if (result.status === "password-error") {
      form.setError("newPassword", { message: result.message });
      return;
    }

    passwordSetRef.current = data.newPassword!;

    if (result.status === "registration-error") {
      toast.error(
        "비밀번호는 설정되었지만 계정 활성화가 완료되지 않았습니다. '시작하기'를 다시 눌러 주세요.",
      );
      return;
    }

    toast.success("계정이 활성화되었습니다.");
    navigate("/");
  };

  // Step별 컴포넌트 렌더링
  const renderStep = () => {
    switch (currentStep) {
      case LoginStep.EMAIL_PASSWORD:
        return (
          <LoginForm isLoading={isLoading || form.formState.isSubmitting} />
        );

      case LoginStep.VERIFICATION:
        return <VerificationCode onCodeComplete={handleVerificationSubmit} />;

      case LoginStep.PASSWORD_RESET:
        return <PasswordReset />;

      default:
        return null;
    }
  };

  return (
    <LoginLayout>
      <FormProvider {...form}>
        <form
          className="flex w-full h-full"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <LoginLeftPanel>{renderStep()}</LoginLeftPanel>
          {currentStep === LoginStep.EMAIL_PASSWORD && <LoginRightPanel />}
        </form>
      </FormProvider>
      <div id="recaptcha-container"></div>
    </LoginLayout>
  );
};

export default LoginPage;
